import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCourseApi } from '../../hooks/useCourseApi';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle, X } from 'lucide-react';

interface UnitEnrollmentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  unitId: number;
  unitName: string;
}

const UnitEnrollmentPopup: React.FC<UnitEnrollmentPopupProps> = ({ 
  isOpen, 
  onClose, 
  unitId, 
  unitName 
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const navigate = useNavigate();
  const { enrollInUnit } = useCourseApi();
  const { isAuthenticated } = useAuth();

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setCode('');
      setError('');
      setSuccess(false);
      setIsClosing(false);
    }, 200);
  };

  const handleCodeSubmit = async () => {
    if (!code.trim()) {
      setError('يرجى إدخال كود الوحدة');
      setSuccess(false);
      return;
    }

    if (!isAuthenticated) {
      setError('يرجى تسجيل الدخول للتسجيل في هذه الوحدة');
      setSuccess(false);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess(false);
      const response = await enrollInUnit(unitId, code);
      
      console.log('Unit enrollment response:', response);
      
      if (response.success) {
        setSuccess(true);
        setError('');
        
        // Show success message for 2 seconds then redirect to my-lectures
        setTimeout(() => {
          setSuccess(false);
          handleClose();
          // Navigate to my-lectures and force refresh by adding a timestamp
          navigate(`/my-lectures?refresh=${Date.now()}`, { replace: true });
        }, 2000);
      } else {
        setSuccess(false);
        setError(response.message || 'كود الوحدة غير صحيح');
      }
    } catch (err) {
      console.log('Error caught:', err);
      setSuccess(false);
      setError('حدث خطأ أثناء التسجيل في الوحدة');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      handleCodeSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div 
        className={`relative w-full max-w-md transform rounded-2xl bg-white shadow-2xl transition-all duration-300 
          ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}
      >
        <div className="p-6 sm:p-8" dir="rtl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              هل تريد فتح محاضرة ({unitName})؟
            </h2>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="space-y-6">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                أدخل كود الوحدة للوصول إلى المحتوى التعليمي
              </p>
            </div>

            <div className="space-y-4">
              <div className="relative">
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="أدخل كود الوحدة"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors duration-200
                    ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-primary'}
                  `}
                  disabled={isLoading}
                />
                {error && (
                  <div className="absolute -bottom-6 right-0 text-red-500 text-sm flex items-center gap-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {error === 'يرجى إدخال كود الوحدة' ? 'يرجى إدخال كود الوحدة' :
                     error === 'يرجى تسجيل الدخول للتسجيل في هذه الوحدة' ? 'يرجى تسجيل الدخول للتسجيل في هذه الوحدة' :
                     error === 'كود الوحدة غير صحيح' ? 'كود الوحدة غير صحيح' :
                     error === 'This unit code has already been used. Please use a different code.' ? 'تم استخدام هذا الكود مسبقاً. يرجى استخدام كود آخر' :
                     error === 'حدث خطأ أثناء التسجيل في الوحدة' ? 'حدث خطأ أثناء التسجيل في الوحدة' :
                     error}
                  </div>
                )}
              </div>

              {/* Success Message */}
              {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-green-600 ml-3" />
                    <p className="text-green-800 font-medium">
                      تم التسجيل بنجاح! جاري التوجيه إلى محاضراتك...
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleCodeSubmit}
                  disabled={isLoading || success}
                  className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-200
                    ${isLoading || success
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-primary text-white hover:bg-primary/90 shadow-lg hover:shadow-primary/25'
                    }
                  `}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      جاري التحقق...
                    </span>
                  ) : success ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      تم بنجاح!
                    </span>
                  ) : (
                    'فتح المحاضرة'
                  )}
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200"
                  disabled={isLoading || success}
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnitEnrollmentPopup;
